/** @jsxImportSource @emotion/react */

import { useState, useEffect, useRef, useContext } from 'react';
import { CSSObject } from '@emotion/react';
import CodeInput from '../../../components/codeInput';
import Title from '../../../components/title';
import request from '../../../utils/request';
import { requestWithStepUp } from '../../../utils/stepUpChallenge';
import { appCheckHeaders } from '../../../utils/appCheckToken';
import apiKeys from '../../../constants/apiKeys';
import useNavigation from '../../../hooks/useNavigation';
import useRequestError from '../../../hooks/useRequestError';
import RequestError from '../../../utils/requestError';
import useLanguage from '../../../hooks/useLanguage';
import Clickable from '../../../components/clickable';
import useTheme from '../../../hooks/useTheme';
import { PasswordLoginContext } from '../../../contexts/passwordLoginContext';
import useOverlay from '../../../hooks/useOverlay';
import { AppContext } from '../../../contexts/appContext';

interface ICodeVerifyResponse {
  state: 'NEW' | 'PASSWORD_REQUIRED' | 'EXISTING' | 'VERIFICATION_REQUIRED';
  deviceId: string;
  sessionId: string;
  enterPasswordScreenResource: string;
  // Whether this account is eligible for France's magic-link password reset:
  // a verified email on file, and in the `magix-link-reset` A/B test. Resolved
  // server-side (view-layer's VerifyDeviceRegistrationSmsCode), as no session
  // exists until the password is verified, and native apps read the same
  // computed flag (carried on enterPasswordScreenResource) so both platforms
  // make the same decision from one source.
  magicLinkResetEnabled?: boolean;
}

const SmsCodeEntryView = () => {
  const { saveSession } = useContext(AppContext);
  const { phone, smsCode, setSmsCode, setVerifiedCode } =
    useContext(PasswordLoginContext);
  const navigation = useNavigation();
  const handleRequestError = useRequestError();
  const overlay = useOverlay();
  const language = useLanguage();
  const { color, font } = useTheme();

  const [timeToResend, setTimeToResend] = useState(60);
  const [throttleMessage, setThrottleMessage] = useState<string | null>(null);
  const [isThrottled, setIsThrottled] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  useEffect(() => {
    if (timeToResend <= 0) {
      if (isThrottled) {
        setIsThrottled(false);
        setThrottleMessage(null);
      }
      return;
    }

    if (isThrottled) {
      setThrottleMessage(
        language.getTemplated('sms_backoff_retry_message', {
          SECONDS: Math.max(0, timeToResend),
        })
      );
    }

    const timeoutId = setTimeout(() => {
      setTimeToResend(timeToResend - 1);
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [timeToResend, isThrottled, language]);

  const verifyCode = useRef(
    async (code: string, countryCode: string, phoneNumber: string) => {
      try {
        const verifyResponse = await request<ICodeVerifyResponse>(
          '/api/view/register/device/verify',
          {
            method: 'POST',
            body: {
              code,
              country_code: countryCode,
              phone_number: phoneNumber,
              pub_key: apiKeys.KRY_PUB,
              timestamp: Date.now(),
            },
          }
        );

        // Emptied whatever the answer: the provider outlives this screen, so a
        // code left in the field is sent again whenever the screen is shown.
        // The branches that still need the code keep it somewhere nothing
        // submits on sight.
        setSmsCode('');

        switch (verifyResponse.state) {
          case 'NEW':
            // The details form replays this code to finish the registration.
            setVerifiedCode(code);

            navigation.push({
              type: 'LOGIN',
              source: {
                loginMethod: 'PASSWORD_LOGIN',
                viewType: 'USER_DETAILS',
              },
            });
            break;

          // Save the session and reinitialize the app.
          case 'EXISTING':
            saveSession(
              verifyResponse.sessionId,
              verifyResponse.deviceId,
              true
            );
            break;

          // Save the session and push the supplied password screen.
          case 'PASSWORD_REQUIRED':
            // The password screen replays this code alongside the password.
            setVerifiedCode(code);

            saveSession(verifyResponse.sessionId, verifyResponse.deviceId);

            navigation.push({
              type: 'LOGIN',
              source: {
                loginMethod: 'PASSWORD_LOGIN',
                viewType: 'PASSWORD_ENTRY',
                screenResource: verifyResponse.enterPasswordScreenResource,
                magicLinkResetEnabled: verifyResponse.magicLinkResetEnabled,
              },
            });
            break;
          case 'VERIFICATION_REQUIRED':
            // The web client currently doesn't support Onfido. The dialog will instruct the user to open Livi app and create new password.
            overlay.presentAlert({
              title: language.get('pin_code_setup_title_signin'),
              message: language.get('create_new_password_message'),
              items: [
                {
                  title: language.get('ok'),
                  onClick: () => {
                    navigation.reinitializeApp();
                  },
                },
              ],
            });
            break;
          default:
            break;
        }
      } catch (e) {
        if (e instanceof RequestError) {
          const vlError = (e.body as any)?.vlError as
            | { code: string; errorMessage: string; retryAfterSeconds?: number }
            | undefined;

          if (vlError?.code === 'sms_backoff') {
            // retryAfterSeconds comes from an untyped payload; guard against
            // non-finite/negative values so the countdown can't become NaN.
            const raw = Number(vlError.retryAfterSeconds);
            const seconds =
              Number.isFinite(raw) && raw >= 0 ? Math.floor(raw) : 60;
            setTimeToResend(seconds);
            setIsThrottled(true);
            setSmsCode('');
            return;
          }

          if (vlError?.code === 'sms_locked') {
            // Clear any in-progress backoff so the countdown effect stops
            // overwriting the neutral locked message.
            setIsThrottled(false);
            setIsLocked(true);
            setThrottleMessage(
              `${language.get('sms_locked_generic_title')}: ${language.get(
                'sms_locked_generic_message'
              )}`
            );
            setSmsCode('');
            return;
          }
        }

        handleRequestError(e);
        setSmsCode('');
      }
    }
  );

  const submittedCode = useRef('');
  const verifyInFlight = useRef(false);

  // Runs on anything that changes `phone`, not only on a keystroke, and a
  // patient has five attempts before the code they hold stops working. The
  // code is forgotten below four digits so retyping a rejected one still
  // submits, and the in-flight flag stops an edit during a slow request
  // sending the same code twice.
  useEffect(() => {
    if (smsCode.length < 4) {
      submittedCode.current = '';
      return;
    }

    if (verifyInFlight.current || submittedCode.current === smsCode) {
      return;
    }

    submittedCode.current = smsCode;
    verifyInFlight.current = true;
    verifyCode
      .current(smsCode, phone.countryCode, phone.phoneNumber)
      .finally(() => {
        verifyInFlight.current = false;
      });
  }, [smsCode, phone]);

  const resendSmsCode = async () => {
    try {
      await requestWithStepUp('/api/view/register/device/sms', {
        method: 'POST',
        headers: await appCheckHeaders(),
        body: {
          phone_number: phone.phoneNumber,
          country_code: phone.countryCode,
        },
      });

      // A new code supersedes whatever came before it, the same way requesting
      // the first one does. Without this an abandoned signup could reach the
      // details form still holding the code the resend just invalidated.
      setSmsCode('');
      setVerifiedCode('');

      // Reset timer after resending code.
      setTimeToResend(60);
    } catch (e) {
      handleRequestError(e);
    }
  };

  const WRAPPER_STYLE: CSSObject = {
    display: 'flex',
    flexDirection: 'column',
    padding: 20,
    height: 'auto',
  };

  const CONTAINER_STYLE: CSSObject = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'center',
    maxWidth: 300,
    marginTop: 44,
  };

  return (
    <div css={WRAPPER_STYLE}>
      <Title
        title={language.get('enter_sms_code_title')}
        subtitle={`${language.get('enter_sms_code_message')}`}
        large
      />

      <div css={CONTAINER_STYLE}>
        {/* Info label sms sent to */}
        <label css={{ textAlign: 'center', marginBottom: 12 }}>
          {`${language.get('sent_to')} +${phone.countryCode}\xa0${
            phone.phoneNumber
          }`}
        </label>

        {/* Sms code input */}
        <CodeInput
          maxLength={4}
          value={smsCode}
          label={language.get('enter_sms_code_title')}
          disabled={isThrottled || isLocked}
          onChange={(e) => {
            setSmsCode(e.currentTarget.value);
          }}
        />

        {throttleMessage && (
          <label css={{ textAlign: 'center', marginTop: 12 }}>
            {throttleMessage}
          </label>
        )}

        {/* Resend sms code action — hidden while hard-locked (a locked user
            must not request more SMS codes) and while throttled (the backoff
            message already shows the countdown; avoid a second one). */}
        {!isLocked && !isThrottled && (
          <div
            css={{
              marginTop: 44,
              display: 'flex',
              textAlign: 'center',
              justifyContent: 'center',
            }}
          >
            {timeToResend > 0 ? (
              <label>
                {`${language.get('resend_code_description')} ${timeToResend}`}
              </label>
            ) : (
              <Clickable
                scale
                css={{
                  backgroundColor: color.LOCAL_WHITE,
                  color: color.PRIMARY,
                  fontFamily: font.FAMILY_BOLD,
                  padding: 8,
                }}
                onClick={resendSmsCode}
              >
                {language.get('resend_sms_code')}
              </Clickable>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SmsCodeEntryView;
